import { maybe, Maybe } from '@passionware/monads';
import { CustomFieldType } from '../../../../__types__';

/**
 * Option for select types. Existing options from API have id; new options (create or newly added in form) have only value.
 */
export type FieldDefinitionOption = { id?: number; value: string };

type BasicFieldDefinition = {
  name: string;
  isRequired: boolean;
  isFilterable: boolean;
};

export type FieldDefinition = BasicFieldDefinition &
  (
    | {
        type: Exclude<
          CustomFieldType,
          | CustomFieldType.MEASUREMENT_NEW
          | CustomFieldType.SINGLE_SELECT
          | CustomFieldType.MULTI_SELECT
        >;
      }
    | {
        type: CustomFieldType.MULTI_SELECT | CustomFieldType.SINGLE_SELECT;
        options: FieldDefinitionOption[];
      }
    | {
        type: CustomFieldType.MEASUREMENT_NEW;
        defaultUnit: string;
        lockUnit: boolean;
      }
  );

export type FieldFormModel = {
  type: Maybe<CustomFieldType>;
  name: string;
  isRequired: boolean;
  isFilterable: boolean;
  select?: {
    options: FieldDefinitionOption[];
  };
  measurement?: {
    defaultUnit: string;
    lockUnit: boolean;
  };
};

export const formFieldModel = {
  fromDefinition: (definition: Maybe<FieldDefinition>): FieldFormModel => {
    if (maybe.isAbsent(definition)) {
      // we need to return a default model for new fields
      return {
        type: null,
        name: '',
        isRequired: false,
        isFilterable: false,
        select: {
          options: [
            { value: 'Option 1' },
            { value: 'Option 2' },
            { value: 'Option 3' }
          ]
        },
        measurement: {
          defaultUnit: 'cm',
          lockUnit: false
        }
      };
    }
    switch (definition.type) {
      case CustomFieldType.SINGLE_SELECT:
      case CustomFieldType.MULTI_SELECT:
        return {
          type: definition.type,
          name: definition.name,
          isRequired: definition.isRequired,
          isFilterable: definition.isFilterable,
          select: {
            options: definition.options
          }
        };
      case CustomFieldType.MEASUREMENT_NEW:
        return {
          type: definition.type,
          name: definition.name,
          isRequired: definition.isRequired,
          isFilterable: definition.isFilterable,
          measurement: {
            defaultUnit: definition.defaultUnit,
            lockUnit: definition.lockUnit
          }
        };
      default:
        return {
          type: definition.type,
          name: definition.name,
          isRequired: definition.isRequired,
          isFilterable: definition.isFilterable
        };
    }
  },
  toDefinition: (model: FieldFormModel): FieldDefinition | null => {
    switch (model.type) {
      case CustomFieldType.SINGLE_SELECT:
      case CustomFieldType.MULTI_SELECT:
        return {
          type: model.type,
          name: model.name,
          isRequired: model.isRequired,
          isFilterable: model.isFilterable,
          options: model.select?.options ?? []
        };
      case CustomFieldType.MEASUREMENT_NEW:
        return {
          type: model.type,
          name: model.name,
          isRequired: model.isRequired,
          isFilterable: model.isFilterable,
          defaultUnit: model.measurement?.defaultUnit ?? '',
          lockUnit: model.measurement?.lockUnit ?? false
        };
      default:
        if (maybe.isAbsent(model.type)) {
          return null;
        }
        return {
          type: model.type,
          name: model.name,
          isRequired: model.isRequired,
          isFilterable: model.isFilterable
        };
    }
  }
};

/**
 * True when `name` matches another field's name (case-insensitive, trimmed).
 * When editing, pass `initialDefinitionName` so the unchanged name is not treated as a duplicate.
 */
export function isDuplicateCustomFieldName(
  name: string,
  existingFieldNames: readonly string[] | undefined,
  initialDefinitionName?: string | null
): boolean {
  const trimmed = name.trim();
  if (!trimmed) {
    return false;
  }
  const lower = trimmed.toLowerCase();
  const reserved = new Set(
    (existingFieldNames ?? []).map(n => n.trim().toLowerCase())
  );
  if (initialDefinitionName?.trim()) {
    reserved.delete(initialDefinitionName.trim().toLowerCase());
  }
  return reserved.has(lower);
}

export type CustomFieldNameRuleMessages = {
  required: string;
  minLength: string;
  duplicate: string;
};

/** React Hook Form `rules` for the custom field name field (required + duplicate check). */
export function buildCustomFieldNameFieldRules(
  messages: CustomFieldNameRuleMessages,
  existingFieldNames: readonly string[] | undefined,
  /** When editing: `initialDefinition.name` so it is not flagged as a duplicate of itself. */
  initialDefinitionName: string | null
) {
  return {
    required: messages.required,
    validate: {
      minLength: (value: string) =>
        value.trim().length >= 3 ? true : messages.minLength,
      duplicate: (value: string) =>
        isDuplicateCustomFieldName(
          value,
          existingFieldNames,
          initialDefinitionName
        )
          ? messages.duplicate
          : true
    }
  };
}
